import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
  Container,
  Button,
  Row,
  Col,
  Table,
  Form,
  Spinner,
  Badge,
} from "react-bootstrap";
import { CSVLink } from "react-csv";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const TransactionFragment = ({ onBack }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", localStorage.getItem("token"));

      let url = `${API_URL}/admin/transactions?page=${currentPage}&limit=${itemsPerPage}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await fetch(url, {
        method: "GET",
        headers: myHeaders,
      });

      if (response.status === 401) {
        localStorage.clear();
        window.location.reload();
        return;
      }

      const data = await response.json();
      setTransactions(data.payments || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, statusFilter, itemsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchTransactions();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge bg="success">Paid</Badge>;
      case "failed":
        return <Badge bg="danger">Failed</Badge>;
      case "created":
        return (
          <Badge bg="warning" text="dark">
            Pending
          </Badge>
        );
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalRevenue = transactions
    .filter((t) => t.status === "paid")
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <Grid
      item
      xs={10}
      style={{
        backgroundColor: "#fff",
        height: "100vh",
        overflowY: "scroll",
        padding: "20px",
      }}
    >
      <Container fluid>
        <Row>
          <Col xs={12}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "15px" }}
              >
                {onBack && (
                  <Button
                    variant="outline-secondary"
                    style={{ borderRadius: "10px", fontWeight: "600" }}
                    onClick={onBack}
                  >
                    ← Back
                  </Button>
                )}
                <h4 style={{ margin: 0 }}>Transactions</h4>
                <span style={{ color: "#888", fontSize: "14px" }}>
                  ({total} total)
                </span>
              </div>
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                {/* Summary cards */}
                <div
                  style={{
                    backgroundColor: "#e8f5e9",
                    padding: "8px 16px",
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Revenue (this page): ₹{totalRevenue.toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <Form.Control
                style={{ borderRadius: "10px", flex: 2 }}
                type="text"
                placeholder="Search by name, email, phone, order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Form.Control
                as="select"
                style={{ borderRadius: "10px", flex: 1 }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="created">Pending</option>
                <option value="failed">Failed</option>
              </Form.Control>
              <Button
                variant="outline-primary"
                style={{ borderRadius: "10px", whiteSpace: "nowrap" }}
              >
                <CSVLink
                  data={transactions.map((t) => ({
                    "Order ID": t.razorpayOrderId,
                    "Payment ID": t.razorpayPaymentId || "—",
                    "Buyer Name": t.buyerName,
                    "Buyer Email": t.buyerEmail,
                    "Buyer Phone": t.buyerPhone || "—",
                    Course: t.course ? t.course.title : "—",
                    "Amount (₹)": t.amount,
                    Status: t.status,
                    Date: formatDate(t.createdAt),
                    Student: t.employee
                      ? `${t.employee.firstName} (${t.employee.email})`
                      : "—",
                  }))}
                  filename={"transactions.csv"}
                  target="_blank"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  Download CSV
                </CSVLink>
              </Button>
            </div>

            {/* Table */}
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner animation="border" variant="primary" />
              </div>
            ) : transactions.length > 0 ? (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Buyer</th>
                      <th>Course</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Razorpay Order ID</th>
                      <th>Razorpay Payment ID</th>
                      <th>Student Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t, index) => (
                      <tr key={t._id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                          {formatDate(t.createdAt)}
                        </td>
                        <td>
                          <div style={{ fontWeight: "600" }}>{t.buyerName}</div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {t.buyerEmail}
                          </div>
                          {t.buyerPhone && (
                            <div style={{ fontSize: "12px", color: "#888" }}>
                              {t.buyerPhone}
                            </div>
                          )}
                        </td>
                        <td>{t.course ? t.course.title : "—"}</td>
                        <td style={{ fontWeight: "600" }}>
                          ₹{t.amount?.toLocaleString("en-IN")}
                        </td>
                        <td>{getStatusBadge(t.status)}</td>
                        <td
                          style={{ fontSize: "12px", fontFamily: "monospace" }}
                        >
                          {t.razorpayOrderId}
                        </td>
                        <td
                          style={{ fontSize: "12px", fontFamily: "monospace" }}
                        >
                          {t.razorpayPaymentId || "—"}
                        </td>
                        <td>
                          {t.employee ? (
                            <div>
                              <div style={{ fontSize: "13px" }}>
                                {t.employee.firstName}
                              </div>
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                {t.employee.email}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "#aaa" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Form.Control
                    as="select"
                    style={{ width: "130px", borderRadius: "10px" }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="15">15 per page</option>
                    <option value="30">30 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </Form.Control>
                  <nav>
                    <ul className="pagination mb-0">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Prev
                        </button>
                      </li>
                      {[...Array(Math.min(totalPages, 10)).keys()].map((p) => {
                        const pageNum = p + 1;
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px",
                  color: "#888",
                }}
              >
                <h5>No transactions found</h5>
                <p>
                  Transactions will appear here when students purchase courses.
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </Grid>
  );
};

export default TransactionFragment;
